import React from "react";
import { Row, Col, Image, Rate, Button, Space, Typography } from "antd";
import { useSelector } from "react-redux";
import { internationalizeNumericValue } from "@ts/utils";
import { IChannel, IProductInfoWidgetProps } from "@ts/common-types";
import { roundToDynamicPrecisionForRating } from "@ts/widget";

const { Title, Text, Paragraph, Link } = Typography;

const ProductInfoWidget: React.FC<IProductInfoWidgetProps> = ({ widgetData }) => {
    const currencySymbol = useSelector(
        (state: IChannel) => state.channelCurrencySymbol.value
    );

    return (
        <Row
            align="middle"
            justify="space-between"
            wrap={false}
            className="d-flex align-items-center px-2 w-100"
        >
            {/* Product Image */}
            <Col className="flex-shrink-0" span={1}>
                <Image
                    src={widgetData?.overview?.product_img}
                    alt="product_img"
                    preview={false}
                    className="rounded"
                    style={{ width: "64px" }}
                />
            </Col>

            {/* Product Details */}
            <Col className="flex-grow-1 ms-2" span={17}>
                <Space direction="vertical" size="small" className="w-100">
                    <Link
                        href={widgetData?.overview?.skuLink}
                        target="_blank"
                        className="link-primary link-offset-2 link-underline-opacity-25 link-underline-opacity-100-hover small"
                    >
                        SKU: {widgetData.overview?.sku}
                    </Link>
                    <Title level={5} className="m-0">
                        {widgetData.overview?.sku_title}
                    </Title>
                    <div className="rating-container">
                        <Text strong>
                            {+widgetData.overview?.rating.toFixed(1)}
                        </Text>
                        <Rate
                            className="rating-star"
                            allowHalf
                            disabled
                            value={roundToDynamicPrecisionForRating(
                                +widgetData.overview?.rating.toFixed(1)
                            )}
                        />
                        <Link href="#" className="ratings-count ms-3 mb-1">
                            {widgetData?.overview?.rating_count &&
                                internationalizeNumericValue(
                                    widgetData.overview?.rating_count
                                )}{" "}
                            ratings
                        </Link>
                    </div>
                    <Paragraph className="text-muted m-0">
                        BSR: {widgetData.overview?.categoryRank} | Category:{" "}
                        <span
                            className={
                                widgetData.overview?.category === "N/A"
                                    ? "small"
                                    : "link-primary link-offset-2 link-underline-opacity-25 link-underline-opacity-100-hover small"
                            }
                        >
                            {widgetData.overview?.category}
                        </span>{" "}
                        | Sub-Category:{" "}
                        <span
                            className={
                                widgetData.overview?.subCategory === "N/A"
                                    ? "small"
                                    : "link-primary link-offset-2 link-underline-opacity-25 link-underline-opacity-100-hover small"
                            }
                        >
                            {widgetData.overview?.subCategory}
                        </span>
                    </Paragraph>
                </Space>
            </Col>

            {/* Price and Availability */}
            <Col className="flex-shrink-0 text-end" span={4}>
                <Space direction="vertical" size="small">
                    <Title level={4} className="m-0">
                        <Text delete className="text-danger fs-6 me-2">
                            {`${currencySymbol} ${
                                typeof widgetData?.overview?.mrp === "number"
                                    ? internationalizeNumericValue(
                                          widgetData?.overview?.mrp
                                      )
                                    : widgetData?.overview?.mrp
                            }`}
                        </Text>
                        {`${currencySymbol} ${
                            typeof widgetData?.overview?.price === "number"
                                ? internationalizeNumericValue(
                                      widgetData.overview?.price
                                  )
                                : widgetData.overview?.price
                        }`}
                    </Title>
                    <Button
                        variant="solid"
                        className={`pe-none ${widgetData.overview?.availability ? "product-overview-in-stock-btn" :
                            "product-overview-out-of-stock-btn"}`}
                    >
                        {widgetData.overview?.availability
                            ? "In Stock"
                            : "Out of Stock"}
                    </Button>
                </Space>
            </Col>
        </Row>
    );
};

export default ProductInfoWidget;